import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { AppShell } from "@/components/AppShell";
import { ProgressBar, StatusPill } from "@/components/Progress";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { STATUS_LABEL, useLocale } from "@/lib/locale";
import type { ProjectSummary } from "@/lib/domain";

const COUNTRIES = ["IT", "ES", "FR", "DE", "AE", "GB", "US"];
const SECTORS = [
  "bagni_spa",
  "hotel_resort",
  "boutique_retail",
  "residenziale",
  "cucine",
  "horeca",
  "other",
];

export default function ProjectsPage() {
  const { user } = useAuth();
  const { t, locale } = useLocale();
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () =>
    api
      .projects()
      .then(setProjects)
      .catch(() => setProjects([]));

  useEffect(() => {
    void load();
  }, []);

  async function onCreate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.createProject({
        name: form.get("name"),
        clientName: form.get("clientName"),
        siteAddress: form.get("siteAddress"),
        city: form.get("city") || undefined,
        country: form.get("country"),
        totalM2: Number(form.get("totalM2")) || undefined,
        sector: form.get("sector"),
        plannedDays: Number(form.get("plannedDays")) || undefined,
      });
      setShowForm(false);
      await load();
    } catch {
      setError("Creazione non riuscita. Controlla i campi.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="brand-kicker">{t.projects}</div>
          <h1 className="font-serif text-2xl text-paper sm:text-3xl">
            {locale === "it" ? "Cantieri in corso" : "Active projects"}
          </h1>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="rounded-full bg-magenta px-4 py-2 text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-paper transition hover:brightness-110"
        >
          {showForm ? "×" : "+ "}
          {t.newProject}
        </button>
      </div>

      {showForm && (
        <form onSubmit={onCreate} className="glass-panel-strong mt-5 grid gap-3 p-5 sm:grid-cols-2">
          <label className="block">
            <span className="field-label">Nome cantiere</span>
            <input name="name" required className="field" />
          </label>
          <label className="block">
            <span className="field-label">Committente</span>
            <input name="clientName" required className="field" />
          </label>
          <label className="block sm:col-span-2">
            <span className="field-label">Indirizzo</span>
            <input name="siteAddress" required className="field" />
          </label>
          <label className="block">
            <span className="field-label">Città</span>
            <input name="city" className="field" placeholder="Madrid" />
          </label>
          <label className="block">
            <span className="field-label">Paese</span>
            <select name="country" className="field" defaultValue="IT">
              {COUNTRIES.map((c) => (
                <option key={c} value={c} className="bg-night">
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="field-label">Settore</span>
            <select name="sector" className="field" defaultValue="other">
              {SECTORS.map((s) => (
                <option key={s} value={s} className="bg-night">
                  {s.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="field-label">Superficie totale (m²)</span>
            <input name="totalM2" type="number" step="0.01" className="field num" />
          </label>
          <label className="block">
            <span className="field-label">Giorni previsti</span>
            <input name="plannedDays" type="number" className="field num" />
          </label>
          {error && <p className="text-xs text-pink sm:col-span-2">{error}</p>}
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={busy}
              className="rounded-full bg-magenta px-5 py-2 text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-paper disabled:opacity-60"
            >
              {busy ? "…" : "Crea cantiere"}
            </button>
          </div>
        </form>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {projects === null && (
          <p className="text-sm text-ice/60">Caricamento…</p>
        )}
        {projects?.length === 0 && (
          <p className="text-sm text-ice/60">Nessun cantiere visibile.</p>
        )}
        {projects?.map((p) => (
          <Link
            key={p.id}
            to={`/projects/${p.id}`}
            className="glass-panel-strong block p-5 transition hover:border-pink/50"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate font-serif text-lg text-paper">
                  {p.name}
                </div>
                <div className="mt-0.5 truncate text-[0.72rem] text-ice/60">
                  {p.clientName} · {p.siteAddress}
                </div>
              </div>
              <StatusPill
                status={p.status}
                label={STATUS_LABEL[locale][p.status] ?? p.status}
              />
            </div>

            <div className="mt-4">
              <ProgressBar progress={p.progress} />
            </div>

            <div className="mt-3 flex gap-4 text-[0.66rem] text-ice/55">
              <span>
                {p.surfaces} superfic{p.surfaces === 1 ? "e" : "i"}
              </span>
              <span>{p.country}</span>
              {p.startDate && (
                <span className="num">{p.startDate.slice(0, 10)}</span>
              )}
            </div>
          </Link>
        ))}
      </div>

      {user?.role === "client" && (
        <p className="mt-4 text-xs text-ice/50">{t.notAvailable}</p>
      )}
    </AppShell>
  );
}
